import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useProfile } from "../contexts/ProfileContext";
import axios from "axios";
import moment from "moment";
import Alert from "./Alert";

function DailyBonus() {
  const profile = useProfile();
  const totalDays = 30;

  // Array of emojis, one per day:
  const emojis = [
    "🌟", "✨", "⚡", "🔥", "🎉", "🥳", "❤️", "💎", "🎁", "💰",
    "🍀", "🏆", "⭐", "🤩", "🎊", "🍓", "🧸", "🦄", "🚀", "💵",
    "☕", "🍪", "🎂", "🍟", "🌮", "🍕", "🎈", "💖", "🎵", "🍰",
  ];

  // We'll treat `profile.streak` as the "current day" index for the user.
  // If streak=0 => user hasn't started, so day 1 is claimable.
  const [currentStreak, setCurrentStreak] = useState(profile.streak || 0);
  const [lastClaimedAt, setLastClaimedAt] = useState(
    profile.lastClaimedAt ? new Date(profile.lastClaimedAt) : null
  );

  // State for displaying the countdown or "Ready"
  const [nextClaimTime, setNextClaimTime] = useState("");

  const [functionError, setFunctionError] = useState("");
	const [functionSuccess, setFunctionSuccess] = useState("");

  useEffect(() => {
    // Whenever `profile` updates, sync local state.
    setCurrentStreak(profile.streak || 0);
    setLastClaimedAt(profile.lastClaimedAt ? new Date(profile.lastClaimedAt) : null);
  }, [profile]);

  // Calculate "can claim again?" -> 24+ hours passed
  const canClaimAgain = () => {
    if (!lastClaimedAt) return true; // no prior claim => can claim
    const hoursSinceLastClaim = moment().diff(moment(lastClaimedAt), "hours");
	if (hoursSinceLastClaim >= 48){
		setLastClaimedAt(null);
		setCurrentStreak(1);

	}
    return hoursSinceLastClaim >= 24;
  };

  // Calculate the moment when the user can next claim (lastClaim + 24h)
  const nextClaimAt = lastClaimedAt
    ? moment(lastClaimedAt).add(24, "hours")
    : null;

  // This effect updates the countdown (nextClaimTime) every second
  useEffect(() => {
    // If never claimed before, or can already claim, just show "Ready"
	
    if (!lastClaimedAt) {
      setNextClaimTime("Ready");
	  return
      
    }

    // Otherwise, set up an interval to count down until nextClaimAt
    const updateTimer = () => {
      const now = moment();
	  if (!lastClaimedAt) {
        // If lastClaimedAt is null, exit early to avoid errors
        return;
      }
      if (now.isSameOrAfter(nextClaimAt)) {
        // It's time to claim
		nextClaimAt.add(24, "hours");
		const diff = nextClaimAt.diff(now); // in ms
        const duration = moment.duration(diff);

        // Extract hours, minutes, seconds
        const h = Math.floor(duration.asHours()).toString().padStart(2, "0");
        const m = duration.minutes().toString().padStart(2, "0");
        const s = duration.seconds().toString().padStart(2, "0");
        setNextClaimTime(`${h}h ${m}m ${s}s`);
      } else {
        const diff = nextClaimAt.diff(now); // in ms
        const duration = moment.duration(diff);

        // Extract hours, minutes, seconds
        const h = Math.floor(duration.asHours()).toString().padStart(2, "0");
        const m = duration.minutes().toString().padStart(2, "0");
        const s = duration.seconds().toString().padStart(2, "0");

        setNextClaimTime(`${h}h ${m}m ${s}s`);
      }
    };

    // Call once immediately
    updateTimer();

    // Then call every second
    const intervalId = setInterval(updateTimer, 1000);

    // Cleanup
    return () => clearInterval(intervalId);
  }, [lastClaimedAt, nextClaimAt]);

  // Click handler to claim a specific day
  const handleClaim = async (dayNumber) => {
    if (!canClaimAgain()) {
      const hoursLeft = 24 - moment().diff(moment(lastClaimedAt), "hours");
      setFunctionError(`You need to wait ${Math.ceil(hoursLeft)} hour(s) to claim the next bonus.`);
      return;
    }

    try {
      // Send request to backend
      const response = await axios.post(
        "https://api.viralgpt.com/dailybonus/claim-bonus",
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      if (response.data && typeof response.data.streak !== "undefined") {
        setCurrentStreak(response.data.streak);
      } else {
        setCurrentStreak((prev) => prev + 1);
      }
      setLastClaimedAt(new Date());

      const bonusAmount = parseInt(dayNumber * (dayNumber / 200) + 1);
      const isPro = profile.socials?.[profile?.selectedProfile]?.planName === 'pro';
      const finalAmount = isPro ? bonusAmount * 2 : bonusAmount;
      
      setFunctionSuccess(`Day ${dayNumber} bonus claimed! You received $${finalAmount} credits.`);

      if (response.data && response.data.message) {
        setFunctionError(response.data.message);
      }
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("Error claiming bonus:", error);
      const errMsg =
        (error.response && error.response.data && error.response.data.error) ||
        "Failed to claim the bonus.";
      setFunctionError(errMsg);
    }
  };

  const daysArray = Array.from({ length: totalDays }, (_, i) => i + 1);

  return (
    <Container className="py-10 justify-items-center text-center">
      <h1 className="text-4xl font-bold text-gray-800">
        Claim Your <span className="text-instagram">Daily Bonus</span>
      </h1>
      <p className="text-muted">

      <Alert 
        variant="danger" 
        message={functionError} 
        show={!!functionError} 
        onClose={() => setFunctionError("")} 
      />

      <Alert 
        variant="success" 
        message={functionSuccess} 
        show={!!functionSuccess} 
        onClose={() => setFunctionSuccess("")} 
      />

	  <div className="ms-2 rounded mb-2 px-2 py-1 text-white bg-instagram"><strong>Current Streak: {currentStreak - 1} </strong>️</div> 
		

		<strong>
		{currentStreak === 1 ? "" : canClaimAgain() ? "Expires in: " : "Next Claim: "}
		</strong>
		 
		 {nextClaimTime === "Ready" ? "Claim your first bonus below!" : nextClaimTime}
		
        <br />
        (If you miss a day, your streak resets to 0)
      </p>

      <Row>
        {daysArray.map((day) => {
          // The logic to determine button state:
          let buttonContent = "Locked";
          let buttonVariant = "secondary";
          let disabled = true;

          // If streak = 0 => show day=1 as claimable, everything else locked.
          if (currentStreak === 1) {
            if (day === 1) {
              // Day 1 is claimable
              if (canClaimAgain()) {
                buttonContent = "Claim Bonus";
                buttonVariant = "  bg-instagram text-white";
                disabled = false;
              } else {
                buttonContent = ""; //Wait
              }
            }
          }
          // If streak > 0 => days < streak are "Claimed", day = streak is claimable (if 24h have passed).
          else {
            if (day < currentStreak) {
              // Already claimed
              buttonContent = "Claimed";
              buttonVariant = " text-white bg-instagram";
            } else if (day === currentStreak) {
              // This is the "active" day to claim
              if (canClaimAgain()) {
                buttonContent = "Claim Bonus";
                buttonVariant = "  bg-instagram text-white";
                disabled = false;
              } else {
                buttonContent = ""; //Wait
              }
            }
            // If day > currentStreak => future day => remain "Locked"
          }

          return (
            <Col xs={6} sm={6} md={4} lg={4} className="mb-3" key={day}>
              <div
                style={{ border: "none" }}
                className="text-center h-100 border bg-white rounded-lg border-none"
              >
                <Card.Body className="p-6">
					
                  <Card.Title className="text-2xl font-bold mt-3">Day {day}</Card.Title>
                  <Card.Text style={{ fontSize: "3rem" }}>
                    {emojis[day - 1]}
                  </Card.Text>
                  <span className="pb-2 d-block">
                    ${parseInt(day * (day / 200) + 1)} Credits {profile.socials?.[profile?.selectedProfile]?.planName === 'pro' && (
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Your Pro plan gives you 2x daily bonus rewards!</Tooltip>}
                      >
                        <strong className="bg-instagram p-1 rounded text-white font-bolder" style={{fontWeight: 900}}>2x</strong>
                      </OverlayTrigger>
                    )}
                  </span>
                  <Button
                    variant={buttonVariant}
                    disabled={disabled}
                    onClick={() => handleClaim(day)}
                  >
                    {buttonContent}{!canClaimAgain() && day === currentStreak && `(${nextClaimTime})`}
                  </Button>
				  <br></br><br></br>
                </Card.Body>
              </div>
            </Col>
          );
        })}
      </Row>
    </Container>
  );
}

export default DailyBonus;
